import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useBrandQuery } from "@/hooks/use-brand";
import { defaultLocale, isAppLocale, type Locale } from "@/i18n/config";
import { readLastLocale, setLastLocale } from "@/i18n/last-locale";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

// The two fetch failures below throw a diagnostic rather than a translated sentence, unlike every
// other hook after POS2-1176. `i18n/provider.tsx` imports this hook, so `useTranslation` here is a
// cycle — `t` would resolve against the default context and return raw keys. Neither string is
// rendered: the GET falls back to the org default, and the PATCH is rolled back optimistically.
const QUERY_KEY = ["user-locale"];

/** The rung of the chain a user inherits from when they have chosen nothing themselves. */
export type InheritedFrom = "organisation" | "browser" | "fallback";

/** The stored preference, or null when the user has never picked one — the "fall through" state.
 *
 * A stored value the app no longer ships (a locale removed from `appLocales`) is treated as
 * absent rather than honoured: the column carries no CHECK by design, so this is a boundary.
 */
function useStoredLocale() {
  const headers = useApiHeaders();

  return useQuery<Locale | null>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/users/me/preferences`, { headers });
      if (!res.ok) throw new Error(`preferences GET ${res.status}`);
      const body = (await res.json()) as { data: { locale: string | null } };
      const stored = body.data.locale;
      return stored && isAppLocale(stored) ? stored : null;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    // The locale this browser resolved last time, so the first paint is the language the user
    // actually reads rather than whatever `navigator.language` says, and so an offline boot —
    // where this fetch cannot answer at all — still renders it.
    //
    // `initialDataUpdatedAt: 0` is what keeps it a seed and not a substitute: it marks the value
    // infinitely stale, so the refetch starts immediately and its answer wins whatever the
    // default `staleTime` is — including the `null` that means "no preference, use the org
    // default", which is how an organisation changing its default still reaches this user.
    initialData: readLastLocale,
    initialDataUpdatedAt: 0,
  });
}

/** `navigator.language` if it names a shipped locale — "de-AT" counts as German. */
function browserLocale(): Locale | null {
  const tag = navigator.language;
  if (isAppLocale(tag)) return tag;
  const base = tag.split("-")[0];
  return isAppLocale(base) ? base : null;
}

/**
 * The active locale and the one way to change it (POS2-1133).
 *
 * Resolution order: the user's own preference, then their organisation's default, then the
 * browser, then English. Deliberately not a JWT claim and not part of the resolved-config chain
 * (see the ticket's pinned decision 8) — language is a person setting with an org default, and
 * at boot there is no entity to resolve config against.
 *
 * The org rung arrives on the brand read, which is the one organisation endpoint every member
 * can call whatever their role. `GET /organisations/me` needs `organisation:read`, held only by
 * Org Admin and Site Manager, so reading the org default there would leave every Staff user in a
 * German organisation looking at English.
 *
 * The write is optimistic: the cache moves first so the app switches language on the click, and
 * rolls back if the PATCH fails. Callers pass `onSuccess`/`onError` for the toast copy, the same
 * shape the notification switch on `/account` already uses.
 */
export function useLocale() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const stored = useStoredLocale();
  const brand = useBrandQuery();
  const orgDefault = brand.data?.default_locale ?? null;

  const inheritedOrg = orgDefault && isAppLocale(orgDefault) ? orgDefault : null;
  const inheritedBrowser = browserLocale();
  const locale = stored.data ?? inheritedOrg ?? inheritedBrowser ?? defaultLocale;

  // Which rung would supply the language if nothing were stored, or **null while that is not yet
  // knowable**. Computed here because this is where the chain lives; re-deriving it in `/account`
  // would be a second copy of the resolution order (POS2-1161).
  //
  // The null matters and is not defensive. The org rung arrives on the brand read, so an absent
  // `default_locale` means either "this organisation has none" or "the read has not answered" —
  // and treating the second as the first names the browser for a moment and then corrects itself,
  // which is the same ambiguity this field exists to remove. `isLoading` is the right test rather
  // than `isPending`: the query is *disabled* when there is no organisation to consult at all (an
  // org-less account, or a platform admin who has picked no org), and a disabled query stays
  // pending forever while never fetching — so gating on `isPending` would leave those users with
  // no explanation at all.
  const inheritedFrom: InheritedFrom | null = brand.isLoading
    ? null
    : inheritedOrg
      ? "organisation"
      : inheritedBrowser
        ? "browser"
        : "fallback";

  // Whatever the chain settled on, so the next boot starts there. Writing the *resolved* locale
  // rather than `stored.data` is the point: a user who has never chosen still gets their
  // organisation's German back instantly, and that is the rung a German rollout leans on.
  useEffect(() => {
    setLastLocale(locale);
  }, [locale]);

  // `Locale | null`, because clearing the preference is what returns a user to their
  // organisation's language (POS2-1161). The null survives the wire: the endpoint applies
  // `model_dump(exclude_unset=True)`, so a field *sent* as null is written while an absent field
  // is left alone — unlike `PATCH /organisations/me`, whose `exclude_none` is exactly why the
  // organisation picker cannot offer the same option.
  const mutation = useMutation<void, Error, Locale | null, { previous: Locale | null }>({
    mutationFn: async (next) => {
      const res = await fetch(`${API_URL}/api/v1/users/me/preferences`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ locale: next }),
      });
      if (!res.ok) throw new Error(`preferences PATCH ${res.status}`);
    },
    onMutate: (next) => {
      const previous = queryClient.getQueryData<Locale | null>(QUERY_KEY) ?? null;
      queryClient.setQueryData(QUERY_KEY, next);
      return { previous };
    },
    onError: (_error, _next, context) => {
      queryClient.setQueryData(QUERY_KEY, context?.previous ?? null);
    },
  });

  return {
    locale,
    /** What the user actually chose, or null when they never have — the state `locale` hides. */
    storedLocale: stored.data ?? null,
    inheritedFrom,
    setLocale: mutation.mutate,
    isSaving: mutation.isPending,
  };
}
